import {
  Box,
  Container,
  HStack,
  Link
} from "@chakra-ui/react";
import { ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { siteFeaturedGame } from "@/data/games";
import { siteConfig } from "@/data/site";
import { EdgePrimitive } from "@/design-system/primitives";
import { nvpTokens } from "@/design-system/tokens";
import { useI18n } from "@/i18n";
import { NeonDieMark } from "@/shared/brand/NeonDieMark";
import { NvpWordmark } from "@/shared/brand/NvpWordmark";
import { GlowButton } from "@/shared/ui/GlowButton";

type NavItem = (typeof siteConfig.nav)[number];
type NavId = NavItem["id"];
type ActiveNavId = NavId | undefined;

function normalizedPath() {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function navIdFromRoute(): ActiveNavId {
  const path = normalizedPath();
  if (path === "/" || path.startsWith("/games/")) {
    return "games";
  }
  return undefined;
}

function navIdFromScroll(): ActiveNavId {
  if (normalizedPath() !== "/") {
    return navIdFromRoute();
  }

  const studioSection = document.getElementById("studio");
  if (
    studioSection
    && studioSection.getBoundingClientRect().top
      <= nvpTokens.landing.header.studioActivationOffsetPx
  ) {
    return "studio";
  }

  return "games";
}

export function Header() {
  const { copy } = useI18n();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeNavId, setActiveNavId] = useState<ActiveNavId>(() =>
    typeof window === "undefined" ? "games" : navIdFromRoute()
  );
  const isScrolledRef = useRef(isScrolled);
  const activeNavIdRef = useRef(activeNavId);
  const frameRef = useRef<number>();
  const navLabel = (id: NavId) => {
    return copy.nav[id as keyof typeof copy.nav];
  };

  useEffect(() => {
    function updateHeaderState() {
      frameRef.current = undefined;
      const nextIsScrolled = window.scrollY > nvpTokens.landing.header.scrollThresholdPx;
      const nextActiveNavId = navIdFromScroll();

      if (isScrolledRef.current !== nextIsScrolled) {
        isScrolledRef.current = nextIsScrolled;
        setIsScrolled(nextIsScrolled);
      }

      if (activeNavIdRef.current !== nextActiveNavId) {
        activeNavIdRef.current = nextActiveNavId;
        setActiveNavId(nextActiveNavId);
      }
    }

    function requestHeaderStateUpdate() {
      if (frameRef.current !== undefined) {
        return;
      }
      frameRef.current = window.requestAnimationFrame(updateHeaderState);
    }

    updateHeaderState();
    window.addEventListener("scroll", requestHeaderStateUpdate, { passive: true });
    window.addEventListener("hashchange", requestHeaderStateUpdate);
    window.addEventListener("popstate", requestHeaderStateUpdate);
    return () => {
      if (frameRef.current !== undefined) {
        window.cancelAnimationFrame(frameRef.current);
      }
      window.removeEventListener("scroll", requestHeaderStateUpdate);
      window.removeEventListener("hashchange", requestHeaderStateUpdate);
      window.removeEventListener("popstate", requestHeaderStateUpdate);
    };
  }, []);

  return (
    <Box
      as="header"
      position="fixed"
      top={0}
      zIndex={20}
      w="100%"
      bg={isScrolled ? nvpTokens.landing.header.scrolledBackground : nvpTokens.landing.header.background}
      backdropFilter={nvpTokens.landing.header.backdropFilter}
      sx={{
        WebkitBackdropFilter: nvpTokens.landing.header.backdropFilter
      }}
      borderBottom="1px solid"
      borderColor={isScrolled ? nvpTokens.landing.header.scrolledBorder : "transparent"}
      boxShadow={isScrolled ? nvpTokens.landing.header.scrolledShadow : "none"}
      transition={`background ${nvpTokens.motion.duration.fast} ${nvpTokens.motion.easing.standard}, border-color ${nvpTokens.motion.duration.fast} ${nvpTokens.motion.easing.standard}, box-shadow ${nvpTokens.motion.duration.fast} ${nvpTokens.motion.easing.standard}`}
    >
      <EdgePrimitive
        tone="pink"
        left="0"
        right="0"
        bottom="0"
        opacity={isScrolled ? nvpTokens.landing.header.edgeOpacity : 0}
        transition={`opacity ${nvpTokens.motion.duration.fast} ${nvpTokens.motion.easing.standard}`}
      />
      <Container maxW={nvpTokens.layout.container} py={4}>
        <HStack justify="space-between" gap={4}>
          <Link href="/" _hover={{ textDecoration: "none" }}>
            <HStack gap={3}>
              <NeonDieMark boxSize={nvpTokens.brand.logo.dieMarkSize} />
              <NvpWordmark />
            </HStack>
          </Link>

          <HStack
            as="nav"
            display={{ base: "none", md: "flex" }}
            gap={7}
            color="whiteAlpha.800"
            fontSize="sm"
            fontWeight={800}
            textTransform="uppercase"
          >
            {siteConfig.nav.map((item) => {
              const isActive = item.id === activeNavId;
              return (
                <Link
                  key={item.id}
                  href={item.href}
                  position="relative"
                  pb={2}
                  color={isActive ? "white" : undefined}
                  onClick={() => {
                    activeNavIdRef.current = item.id;
                    setActiveNavId(item.id);
                  }}
                  _hover={{ color: "white", textDecoration: "none" }}
                  _after={{
                    content: "\"\"",
                    position: "absolute",
                    left: "50%",
                    bottom: 0,
                    h: "6px",
                    w: "6px",
                    borderRadius: "999px",
                    bg: "nvp.pink",
                    boxShadow: "0 0 12px #ff4fd8",
                    opacity: isActive ? 1 : 0,
                    transform: `translateX(-50%) scale(${isActive ? 1 : 0.35})`,
                    transition: "opacity 160ms ease, transform 160ms ease"
                  }}
                >
                  {navLabel(item.id)}
                </Link>
              );
            })}
          </HStack>

          <HStack>
            <GlowButton
              as="a"
              href={`/games/${siteFeaturedGame.slug}`}
              glow="pink"
              motion="glowPulse"
              display={{ base: "none", sm: "inline-flex" }}
              minH="46px"
              px={6}
              size="sm"
              rightIcon={<ChevronRight size={17} color="#ff4fd8" strokeWidth={3} />}
            >
              PLAY NOW
            </GlowButton>
          </HStack>
        </HStack>
      </Container>
    </Box>
  );
}
